import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  ArrowLeft, Star, ShoppingBag, Bookmark, Flag, Tag, 
  HelpCircle, MessageSquare, Heart, ChevronRight, MapPin, Loader2, Store, Image as ImageIcon, X
} from "lucide-react";
import { 
  getListingById, getListings, toggleBookmark, 
  reportContent, resolveImageUrl, getListingReviews, createListingReview,
  getSellerReviews, uploadImage
} from "@/lib/api";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import api from "@/lib/api";

function StarRating({ rating, onRatingChange, interactive = false, size = "md" }) {
  const sizeClasses = {
    sm: "w-3 h-3",
    md: "w-5 h-5",
    lg: "w-6 h-6"
  };
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRatingChange?.(star)}
          className={interactive ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}
        >
          <Star 
            className={`${sizeClasses[size]} ${star <= rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#E5E0D5]'}`} 
          />
        </button>
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  return (
    <Card className="bg-white border-[#E5E0D5]">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Avatar className="w-10 h-10">
            <AvatarImage src={resolveImageUrl(review.reviewer?.picture)} />
            <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm">
              {review.reviewer?.name?.[0]?.toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="font-['Nunito'] font-semibold text-[#2D2A26] text-sm">
                {review.reviewer?.name || "Anonymous"}
              </span>
              <span className="text-xs text-[#6B665E] font-['Nunito']">
                {new Date(review.created_at).toLocaleDateString()}
              </span>
            </div>
            <StarRating rating={review.rating} size="sm" />
            {review.comment && (
              <p className="text-sm text-[#6B665E] font-['Nunito'] mt-2">{review.comment}</p>
            )}
            {review.media_url && (
              <img 
                src={resolveImageUrl(review.media_url)} 
                alt="Review photo" 
                className="mt-3 rounded-lg max-h-48 object-cover border border-[#E5E0D5]"
              />
            )}
            {review.listing_title && (
              <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1 italic">
                Review for: {review.listing_title}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MiniListingCard({ listing, onClick }) {
  return (
    <Card 
      className="bg-white border-[#E5E0D5] hover:shadow-md transition-shadow cursor-pointer overflow-hidden h-full flex flex-col"
      onClick={onClick}
    >
      <div className="relative w-full" style={{ paddingBottom: '125%' }}>
        {listing.image_url ? (
          <img 
            src={resolveImageUrl(listing.image_url)} 
            alt={listing.title} 
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 w-full h-full bg-[#F0EBE0] flex items-center justify-center">
            <ShoppingBag className="w-8 h-8 text-[#E5E0D5]" />
          </div>
        )}
      </div>
      <CardContent className="p-3 flex flex-col flex-1">
        <h4 className="font-['Crimson_Text'] text-sm font-semibold text-[#2D2A26] line-clamp-1 mb-1">{listing.title}</h4>
        <span className="font-['Nunito'] font-bold text-[var(--liturgical-primary)] text-sm">${listing.price}</span>
      </CardContent>
    </Card>
  );
}

export default function ListingDetailPage({ currentUser }) {
  const { listingId } = useParams();
  const navigate = useNavigate();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookmarked, setBookmarked] = useState(false);
  
  const [listingReviews, setListingReviews] = useState([]);
  const [sellerReviews, setSellerReviews] = useState([]);
  const [sellerAverageRating, setSellerAverageRating] = useState(0);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [sellerListings, setSellerListings] = useState([]);
  const [similarListings, setSimilarListings] = useState([]);
  
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [newReviewRating, setNewReviewRating] = useState(5);
  const [newReviewComment, setNewReviewComment] = useState("");
  const [newReviewMedia, setNewReviewMedia] = useState(null);
  const [uploadingReviewMedia, setUploadingReviewMedia] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const reviewFileInputRef = useRef(null);
  
  // Message seller dialog state
  const [showMessageSellerDialog, setShowMessageSellerDialog] = useState(false);
  const [messagingSellerLoading, setMessagingSellerLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadListing();
  }, [listingId]);

  useEffect(() => {
    if (listing) {
      loadListingReviews();
      loadSellerReviews();
      loadSellerListings();
      loadSimilarListings();
    }
  }, [listing]);

  const loadListing = async () => {
    setLoading(true);
    try {
      const res = await getListingById(listingId);
      setListing(res.data);
    } catch (err) {
      toast.error("Failed to load listing");
      navigate("/marketplace");
    }
    setLoading(false);
  };

  const loadListingReviews = async () => {
    setLoadingReviews(true);
    try {
      const res = await getListingReviews(listingId);
      setListingReviews(res.data || []);
    } catch (err) {
      console.error("Failed to load listing reviews:", err);
    }
    setLoadingReviews(false);
  };

  const loadSellerReviews = async () => {
    if (!listing?.seller?.user_id) return;
    try {
      const res = await getSellerReviews(listing.seller.user_id);
      const reviews = res.data?.reviews || [];
      setSellerReviews(reviews);
      setSellerAverageRating(res.data?.average_rating || 0);
    } catch (err) {
      console.error("Failed to load seller reviews:", err);
    }
  };

  const loadSellerListings = async () => {
    if (!listing?.seller?.user_id) return;
    try {
      let otherListings = [];
      if (listing.shop?.shop_id) {
        // Fetch from the shop's listings endpoint
        const res = await api.get(`/shops/${listing.shop.shop_id}/listings`, { params: { limit: 5 } });
        otherListings = (res.data.listings || []).filter(l => l.listing_id !== listingId);
      } else {
        // Fallback: filter by seller_id
        const res = await getListings({ seller_id: listing.seller.user_id });
        otherListings = (res.data.listings || res.data || []).filter(l => l.listing_id !== listingId);
      }
      setSellerListings(otherListings.slice(0, 4));
    } catch (err) {
      console.error("Failed to load seller listings:", err);
    }
  };

  const loadSimilarListings = async () => {
    if (!listing?.category) return;
    try {
      const res = await getListings({ category: listing.category, exclude_seller: listing.seller?.user_id });
      const similar = (res.data.listings || res.data || [])
        .filter(l => l.listing_id !== listingId);
      setSimilarListings(similar.slice(0, 4));
    } catch (err) {
      console.error("Failed to load similar listings:", err);
    }
  };

  const handleBookmark = async () => {
    try {
      const res = await toggleBookmark({ content_type: "listing", content_id: listing.listing_id });
      setBookmarked(res.data.bookmarked);
      
      if (res.data.bookmarked && !isOwnListing) {
        // Show enhanced toast with "Message Seller" option
        toast.success(
          <div className="flex flex-col gap-2">
            <span className="font-semibold">Saved to bookmarks!</span>
            <span className="text-sm text-gray-600">Interested? Contact the seller directly.</span>
            <button 
              onClick={() => handleMessageSeller()}
              className="mt-1 px-3 py-1.5 bg-[var(--liturgical-primary)] text-white rounded-md text-sm font-medium hover:brightness-90 transition-all"
            >
              Message Seller
            </button>
          </div>,
          { duration: 5000 }
        );
      } else if (!res.data.bookmarked) {
        toast.success("Removed from bookmarks");
      }
    } catch {
      toast.error("Failed to bookmark");
    }
  };

  const handleReport = async () => {
    if (window.confirm("Report this listing as inappropriate?")) {
      try {
        await reportContent({ content_type: "listing", content_id: listing.listing_id, reason: "inappropriate" });
        toast.success("Report submitted");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to report");
      }
    }
  };

  const handleSubmitReview = async () => {
    if (!newReviewRating) {
      toast.error("Please select a rating");
      return;
    }
    setSubmittingReview(true);
    try {
      await createListingReview(listingId, {
        rating: newReviewRating,
        comment: newReviewComment,
        media_url: newReviewMedia
      });
      toast.success("Review submitted!");
      setShowReviewDialog(false);
      setNewReviewRating(5);
      setNewReviewComment("");
      setNewReviewMedia(null);
      loadListingReviews();
      loadSellerReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    }
    setSubmittingReview(false);
  };
  
  const handleReviewMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    setUploadingReviewMedia(true);
    try {
      const res = await uploadImage(file);
      const url = res.data?.url || res.data?.data?.url;
      if (url) {
        setNewReviewMedia(url);
        toast.success("Image uploaded");
      }
    } catch (err) {
      toast.error("Failed to upload image");
    }
    setUploadingReviewMedia(false);
  };
  
  const getAutoMessage = () => {
    if (!listing) return "";
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/listing/${listing.listing_id}`;
    return `Hi, I'm interested in your listing: "${listing.title}"\n\n${link}`;
  };
  
  const handleMessageSeller = () => {
    if (!listing?.seller?.user_id) return;
    setShowMessageSellerDialog(true);
  };
  
  const handleConfirmMessageSeller = async () => {
    if (!listing?.seller?.user_id) return;
    
    setMessagingSellerLoading(true);
    try {
      const message = getAutoMessage();
      await api.post("/messages/send", {
        receiver_id: listing.seller.user_id,
        content: message
      });
      toast.success("Message sent to seller!");
      setShowMessageSellerDialog(false);
      navigate(`/messages/${listing.seller.user_id}`);
    } catch (err) {
      // If conversation already exists or error, still navigate
      setShowMessageSellerDialog(false);
      navigate(`/messages/${listing.seller.user_id}`);
    }
    setMessagingSellerLoading(false);
  };

  const listingAverageRating = listingReviews.length > 0 
    ? (listingReviews.reduce((sum, r) => sum + r.rating, 0) / listingReviews.length).toFixed(1)
    : 0;

  const isSold = listing?.status === "sold";
  const isOwnListing = listing?.seller?.user_id === currentUser?.user_id;
  
  // Check if there's content to show
  const hasListingReviews = listingReviews.length > 0;
  const hasSellerReviews = sellerReviews.length > 0;
  const hasFaqs = listing?.custom_faq;
  const hasAnyTabContent = hasListingReviews || hasSellerReviews || hasFaqs;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <p className="text-[#6B665E] font-['Nunito']">Listing not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/marketplace")}
          className="mb-4 text-[#6B665E] hover:text-[#2D2A26]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace
        </Button>

        {/* Main Product Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Product Image */}
          <div className="relative">
            <div className="relative w-full rounded-xl overflow-hidden bg-white shadow-sm" style={{ paddingBottom: '100%' }}>
              {listing.image_url ? (
                <img 
                  src={resolveImageUrl(listing.image_url)} 
                  alt={listing.title} 
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 w-full h-full bg-[#F0EBE0] flex items-center justify-center">
                  <ShoppingBag className="w-16 h-16 text-[#E5E0D5]" />
                </div>
              )}
              {isSold && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="bg-[var(--liturgical-primary)] text-white px-6 py-2 rounded-full font-['Nunito'] font-semibold text-lg">SOLD</span>
                </div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-4">
            <div>
              <div className="flex items-start justify-between gap-4 mb-2">
                <h1 className="font-['Crimson_Text'] text-2xl md:text-3xl font-bold text-[#2D2A26]">{listing.title}</h1>
                <div className="flex gap-2">
                  <button
                    onClick={handleBookmark}
                    className={`p-2 rounded-full border transition-colors ${bookmarked ? 'border-[var(--liturgical-secondary)] text-[var(--liturgical-secondary)]' : 'border-[#E5E0D5] text-[#6B665E] hover:text-[var(--liturgical-secondary)]'}`}
                  >
                    <Bookmark className={`w-5 h-5 ${bookmarked ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    onClick={handleReport}
                    className="p-2 rounded-full border border-[#E5E0D5] text-[#6B665E] hover:text-[#991B1B] transition-colors"
                  >
                    <Flag className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <span className="font-['Nunito'] font-bold text-[var(--liturgical-primary)] text-3xl">${listing.price}</span>
              
              {/* Show ratings summary if available */}
              {(hasListingReviews || hasSellerReviews) && (
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  {hasListingReviews && (
                    <div className="flex items-center gap-1">
                      <StarRating rating={Math.round(parseFloat(listingAverageRating))} size="sm" />
                      <span className="text-sm text-[#6B665E] font-['Nunito']">
                        {listingAverageRating} ({listingReviews.length})
                      </span>
                    </div>
                  )}
                  {hasSellerReviews && (
                    <div className="flex items-center gap-1 text-sm text-[#6B665E] font-['Nunito']">
                      <span>Seller:</span>
                      <StarRating rating={Math.round(sellerAverageRating)} size="sm" />
                      <span>{sellerAverageRating.toFixed(1)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] font-['Nunito']">
                <Tag className="w-3 h-3 mr-1" />{listing.category}
              </Badge>
              {listing.condition && (
                <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] font-['Nunito']">
                  {listing.condition === 'new' && 'New'}
                  {listing.condition === 'like_new' && 'Like New'}
                  {listing.condition === 'good' && 'Good'}
                  {listing.condition === 'fair' && 'Fair'}
                </Badge>
              )}
              {listing.listing_type === "service" && (
                <Badge className="bg-[var(--liturgical-primary)] text-white font-['Nunito']">Service</Badge>
              )}
              {listing.parish_name && (
                <Badge variant="outline" className="border-[var(--liturgical-secondary)] text-[var(--liturgical-secondary)] font-['Nunito']">
                  {listing.parish_name}
                </Badge>
              )}
            </div>

            {listing.location && (
              <div className="flex items-center gap-2 text-sm text-[#6B665E] font-['Nunito']">
                <MapPin className="w-4 h-4" />
                <span>{listing.location}</span>
              </div>
            )}

            <div 
              className="text-[#6B665E] font-['Nunito'] leading-relaxed prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(listing.description || '') }}
            />

            {/* Shop Info */}
            {listing.shop && (
              <Card 
                className="bg-white border-[#E5E0D5] cursor-pointer hover:border-[var(--liturgical-primary)] transition-colors"
                onClick={() => navigate(`/shop/${listing.shop.shop_id}`)}
                data-testid="listing-shop-card"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {listing.shop.logo_url ? (
                      <img 
                        src={resolveImageUrl(listing.shop.logo_url)} 
                        alt={listing.shop.name} 
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-[#F0EBE0] flex items-center justify-center">
                        <Store className="w-6 h-6 text-[var(--liturgical-primary)]" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-['Nunito'] font-semibold text-[#2D2A26]">{listing.shop.name}</p>
                      <p className="text-sm text-[var(--liturgical-primary)] font-['Nunito']">View Shop</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#6B665E]" />
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Seller Info */}
            {listing.seller && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Avatar 
                      className="w-12 h-12 cursor-pointer"
                      onClick={() => navigate(`/profile/${listing.seller.user_id}`)}
                    >
                      <AvatarImage src={resolveImageUrl(listing.seller.picture)} />
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white">
                        {listing.seller.name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-['Nunito'] font-semibold text-[#2D2A26]">{listing.seller.name}</p>
                      <p className="text-sm text-[#6B665E] font-['Nunito']">Seller</p>
                      {hasSellerReviews && (
                        <div className="flex items-center gap-1 mt-1">
                          <StarRating rating={Math.round(sellerAverageRating)} size="sm" />
                          <span className="text-xs text-[#6B665E] font-['Nunito']">
                            {sellerAverageRating.toFixed(1)} ({sellerReviews.length} reviews)
                          </span>
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={handleMessageSeller}
                      className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Message Seller
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Tabs Section - Only show if there's content */}
        {hasAnyTabContent && (
          <Tabs defaultValue={hasFaqs ? "faqs" : hasListingReviews ? "listing-reviews" : "seller-reviews"} className="mb-8">
            <TabsList className="bg-white border border-[#E5E0D5] p-1 w-full flex flex-wrap h-auto">
              {hasFaqs && (
                <TabsTrigger value="faqs" className="flex-1 font-['Nunito'] data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
                  <HelpCircle className="w-4 h-4 mr-1" /> FAQs
                </TabsTrigger>
              )}
              {hasListingReviews && (
                <TabsTrigger value="listing-reviews" className="flex-1 font-['Nunito'] data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
                  <Star className="w-4 h-4 mr-1" /> Listing Reviews ({listingReviews.length})
                </TabsTrigger>
              )}
              {hasSellerReviews && (
                <TabsTrigger value="seller-reviews" className="flex-1 font-['Nunito'] data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
                  <Star className="w-4 h-4 mr-1" /> Seller Reviews ({sellerReviews.length})
                </TabsTrigger>
              )}
            </TabsList>

            {/* FAQs Tab */}
            {hasFaqs && (
              <TabsContent value="faqs" className="mt-4">
                <Card className="bg-white border-[#E5E0D5]">
                  <CardContent className="p-6 space-y-4">
                    <h3 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">Additional Information</h3>
                    <div className="p-4 bg-[#F9F7F2] rounded-lg">
                      <div 
                        className="text-sm text-[#6B665E] font-['Nunito'] prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(listing.custom_faq || '') }}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Listing Reviews Tab */}
            {hasListingReviews && (
              <TabsContent value="listing-reviews" className="mt-4">
                <Card className="bg-white border-[#E5E0D5]">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">Listing Reviews</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <StarRating rating={Math.round(parseFloat(listingAverageRating))} />
                          <span className="text-sm text-[#6B665E] font-['Nunito']">
                            {listingAverageRating} out of 5 ({listingReviews.length} review{listingReviews.length !== 1 ? 's' : ''})
                          </span>
                        </div>
                      </div>
                      
                      {!isOwnListing && (
                        <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
                          <DialogTrigger asChild>
                            <Button className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
                              <Star className="w-4 h-4 mr-2" />
                              Write a Review
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-white">
                            <DialogHeader>
                              <DialogTitle className="font-['Crimson_Text'] text-xl">Write a Review</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-4">
                              <div>
                                <p className="font-['Nunito'] font-semibold text-[#2D2A26] mb-2">Your Rating</p>
                                <StarRating 
                                  rating={newReviewRating} 
                                  onRatingChange={setNewReviewRating}
                                  interactive
                                  size="lg"
                                />
                              </div>
                              <div>
                                <p className="font-['Nunito'] font-semibold text-[#2D2A26] mb-2">Your Review</p>
                                <Textarea
                                  value={newReviewComment}
                                  onChange={(e) => setNewReviewComment(e.target.value)}
                                  placeholder="Share your experience..."
                                  className="font-['Nunito'] min-h-[100px]"
                                />
                              </div>
                              <Button 
                                onClick={handleSubmitReview}
                                disabled={submittingReview}
                                className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                              >
                                {submittingReview ? "Submitting..." : "Submit Review"}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>

                    <div className="space-y-4">
                      {listingReviews.map((review, index) => (
                        <ReviewCard key={review.review_id || index} review={review} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Seller Reviews Tab */}
            {hasSellerReviews && (
              <TabsContent value="seller-reviews" className="mt-4">
                <Card className="bg-white border-[#E5E0D5]">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">Seller Reviews</h3>
                        <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
                          All reviews for {listing.seller?.name}'s listings
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <StarRating rating={Math.round(sellerAverageRating)} />
                          <span className="text-sm text-[#6B665E] font-['Nunito']">
                            {sellerAverageRating.toFixed(1)} out of 5 ({sellerReviews.length} review{sellerReviews.length !== 1 ? 's' : ''})
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {sellerReviews.map((review, index) => (
                        <ReviewCard key={review.review_id || index} review={review} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        )}

        {/* Write Review Button - Show if no listing reviews yet */}
        {!hasListingReviews && !isOwnListing && (
          <div className="mb-8">
            <Card className="bg-white border-[#E5E0D5]">
              <CardContent className="p-6 text-center">
                <Star className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">No Reviews Yet</h3>
                <p className="text-sm text-[#6B665E] font-['Nunito'] mb-4">Be the first to review this listing!</p>
                <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
                      <Star className="w-4 h-4 mr-2" />
                      Write a Review
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white">
                    <DialogHeader>
                      <DialogTitle className="font-['Crimson_Text'] text-xl">Write a Review</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <p className="font-['Nunito'] font-semibold text-[#2D2A26] mb-2">Your Rating</p>
                        <StarRating 
                          rating={newReviewRating} 
                          onRatingChange={setNewReviewRating}
                          interactive
                          size="lg"
                        />
                      </div>
                      <div>
                        <p className="font-['Nunito'] font-semibold text-[#2D2A26] mb-2">Your Review</p>
                        <Textarea
                          value={newReviewComment}
                          onChange={(e) => setNewReviewComment(e.target.value)}
                          placeholder="Share your experience..."
                          className="font-['Nunito'] min-h-[100px]"
                        />
                      </div>
                      {/* Photo upload */}
                      <div>
                        <p className="font-['Nunito'] font-semibold text-[#2D2A26] mb-2">Add Photo (optional)</p>
                        {newReviewMedia ? (
                          <div className="relative inline-block">
                            <img src={resolveImageUrl(newReviewMedia)} alt="" className="h-24 rounded-md border border-[#E5E0D5]" />
                            <button
                              type="button"
                              onClick={() => setNewReviewMedia(null)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => reviewFileInputRef.current?.click()}
                            disabled={uploadingReviewMedia}
                            className="border-[#E5E0D5] font-['Nunito'] text-sm"
                          >
                            {uploadingReviewMedia ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                            Upload Photo
                          </Button>
                        )}
                        <input
                          ref={reviewFileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleReviewMediaUpload}
                        />
                      </div>
                      <Button 
                        onClick={handleSubmitReview}
                        disabled={submittingReview || uploadingReviewMedia}
                        className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                      >
                        {submittingReview ? "Submitting..." : "Submit Review"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </div>
        )}

        {/* More from Seller */}
        {sellerListings.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">
                More from {listing.shop?.name || listing.seller?.name}
              </h2>
              <Button 
                variant="ghost" 
                onClick={() => listing.shop?.shop_id ? navigate(`/shop/${listing.shop.shop_id}`) : navigate(`/marketplace?seller=${listing.seller?.user_id}`)}
                className="text-[var(--liturgical-primary)] font-['Nunito']"
              >
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {sellerListings.map(item => (
                <MiniListingCard 
                  key={item.listing_id} 
                  listing={item} 
                  onClick={() => navigate(`/listing/${item.listing_id}`)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Similar Items */}
        {similarListings.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">
                <Heart className="w-5 h-5 inline mr-2 text-[var(--liturgical-primary)]" />
                Other Items You Might Like
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {similarListings.map(item => (
                <MiniListingCard 
                  key={item.listing_id} 
                  listing={item} 
                  onClick={() => navigate(`/listing/${item.listing_id}`)}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Message Seller Confirmation Dialog */}
        <Dialog open={showMessageSellerDialog} onOpenChange={setShowMessageSellerDialog}>
          <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
                Message Seller
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-[#6B665E] font-['Nunito']">
                The following message will be sent to {listing?.seller?.name || 'the seller'}:
              </p>
              <div className="p-3 bg-[#F9F7F2] rounded-md border border-[#E5E0D5]">
                <p className="text-sm font-['Nunito'] text-[#2D2A26] whitespace-pre-line">
                  {getAutoMessage()}
                </p>
              </div>
              <p className="text-xs text-[#6B665E] font-['Nunito']">
                You can add more details after sending this initial message.
              </p>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowMessageSellerDialog(false)}
                  className="flex-1 border-[#E5E0D5] font-['Nunito']"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleConfirmMessageSeller}
                  disabled={messagingSellerLoading}
                  className="flex-1 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                >
                  {messagingSellerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Message"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
